import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { Directory } from '@capacitor/filesystem';
import { ConnectionStatus } from '@capacitor/network';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { HelperFunctions, IListing, IPhoto, ISubPhoto, NGRX_NO_ACTION, Site } from '@curbnturf/entities';
import { IPhotoForm } from '@curbnturf/form-helpers';
import { listingComplete } from '@curbnturf/helpers';
import { ImageUploadOptions } from '@curbnturf/image-processing';
import { ImageRecropModalComponent } from '@curbnturf/image-processing/src/lib/image-recrop/image-recrop-modal.component';
import { ImageUploadComponent } from '@curbnturf/image-processing/src/lib/image-upload/image-upload.component';
import * as ListingActionTypes from '@curbnturf/listing/src/lib/+state/listing.actions';
import * as NetworkSelectors from '@curbnturf/network';
import { OfflineSitePhotosDbSet } from '@curbnturf/network';
import { Logger } from '@curbnturf/network/src/lib/log/logger';
import { PhotoService } from '@curbnturf/photo';
import { SignUpStateFacade } from '@curbnturf/sign-up-state/src/lib/+state/sign-up-state.facade';
import * as SignUpSelectors from '@curbnturf/sign-up-state/src/lib/+state/sign-up-state.selectors';
import { SiteFacade } from '@curbnturf/site';
import * as SiteActions from '@curbnturf/site/src/lib/+state/site.actions';
import { StatusFacade } from '@curbnturf/status';
import { DeleteConfirmationModalComponent } from '@curbnturf/ui/src/lib/delete-confirmation-modal/delete-confirmation-modal.component';
import { IonicModule, ModalController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DragulaModule } from 'ng2-dragula';
import { Subscription } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-site-photos',
  templateUrl: './site-photos.component.html',
  styleUrls: ['./site-photos.component.scss'],
  standalone: true,
  imports: [
    DeleteConfirmationModalComponent,
    DragulaModule,
    FormsModule,
    ImageUploadComponent,
    MiscSvgComponent,
    NgFor,
    NgIf,
    ReactiveFormsModule,
    IonicModule,
  ],
  providers: [AlertFacade, Logger],
})
export class SitePhotosComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup<{ photos: FormArray<FormGroup<IPhotoForm>> }>;
  @Input() site?: Site;
  @Input() autoSave: boolean = true;

  @Output() beforeUpdating: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() imageUpdated: EventEmitter<void> = new EventEmitter<void>();

  uploadOptions: ImageUploadOptions = {
    action: SiteActions.uploadSiteImage,
    completeAction: SiteActions.siteImagesUpdated,
    croppieOptions: {
      // viewport: { height: 280, width: 652 },
      // boundary: { height: 300, width: 698 },
    },
    output: {
      size: { height: 546, width: 1270 },
      circle: false,
    },
  };

  connectionStatus: ConnectionStatus;

  listingComplete = listingComplete;

  webPhotos: { key: string; uri: string; caption?: string }[] = [];

  recropPhotoSelected: ISubPhoto | undefined = undefined;

  get offlineMode() {
    if (Capacitor.isNativePlatform()) {
      if (!this.connectionStatus.connected) {
        return true;
      }
    }
    return false;
  }

  get photos() {
    if (this.form) {
      return this.form.get('photos') as FormArray<FormGroup<IPhotoForm>>;
    }

    return;
  }

  get siteId(): number | undefined {
    if (this.site && this.site.id) {
      return this.site.id;
    }

    return;
  }

  get syncId(): string | undefined {
    if (this.site && this.site.syncId) {
      return this.site.syncId;
    }

    return;
  }

  subscriptions: Subscription[] = [];

  constructor(
    private updates$: Actions,
    private cd: ChangeDetectorRef,
    private offlineSitePhotosDbSet: OfflineSitePhotosDbSet,
    private photoService: PhotoService,
    private signUpFacade: SignUpStateFacade,
    private siteFacade: SiteFacade,
    private statusFacade: StatusFacade,
    private store: Store,
    private modalController: ModalController,
    private alertFacade: AlertFacade,
    private logger: Logger,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.statusFacade.networkStatus$.subscribe((status) => {
        this.connectionStatus = status;
      }),
      this.updates$
        .pipe(
          ofType(ListingActionTypes.listingImagesUpdated),
          withLatestFrom(this.store.select(NetworkSelectors.getStatus)),
          withLatestFrom(this.store.select(SignUpSelectors.getListing)),
        )
        .subscribe(async ([[action, status], listing]) => {
          if (!status.connected) {
            const photos = [...(listing?.photos || [])];

            for (let photo of action.photos) {
              const uri = await this.loadPhotoFromFile(photo.key);
              this.webPhotos.push({ ...photo, key: photo.key, uri, caption: photo.caption });
              // Push the photo to the sign-up listing record.
              photos.push(photo);
            }

            if (listing && listing.photos) {
              this.signUpFacade.updateListingPhotos(photos, listing.id);
            }

            this.cd.detectChanges();
          }
        }),
    );
  }

  async ngOnChanges() {
    this.updateSiteId();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  getPhotoUrl(photo: FormGroup<IPhotoForm>) {
    return HelperFunctions.getPhotoUrlFlatSmall(photo.value as ISubPhoto);
  }

  removePhoto(index: number): void {
    if (this.siteId) {
      const photo = this.photos?.at(index);
      if (photo?.value?.key?.startsWith('listing/')) {
        this.photos?.removeAt(index);
      } else {
        this.siteFacade.removePhoto(this.siteId, index);
      }

      this.form.markAsDirty();
    }
  }

  removePhotoFunction(index: number): () => void {
    return () => this.removePhoto(index);
  }

  movePhotoUp(index: number): void {
    if (index === 0 || !this.photos) {
      return;
    }
    const photo = this.photos.at(index);
    this.photos.removeAt(index);
    this.photos.insert(index - 1, photo);
    this.save(false);
  }

  movePhotoDown(index: number): void {
    if (this.photos) {
      const photo = this.photos.at(index);
      this.photos.removeAt(index);
      this.photos.insert(index + 1, photo);
      this.save(false);
    }
  }

  photoOrder(index: number, item: FormGroup<IPhotoForm>) {
    return item?.value?.key;
  }

  updatePhotoOrder(photos: IPhoto[]) {
    photos.forEach((photo, index) => this.photos?.at(index).patchValue(photo));
    this.photos?.markAsDirty();
  }

  save(imageUpload = true) {
    this.beforeUpdating.emit(imageUpload);
    if (this.siteId) {
      this.signUpFacade.updateSitePhotoDetails(this.siteId, this.form.value.photos as IPhoto[]);
    } else {
      this.alertFacade.display({
        title: 'No Selected Site',
        body: 'There is no site selected to modify.',
        level: 'error',
      });
    }
    this.form.markAsPristine();
  }

  private getListingToSave(): IListing {
    if (!this.site) {
      this.site = new Site();
    }

    return {
      ...this.site,
      // This has to be simple as any undefined fields here would overwrite the good data in the site record.
      ...(this.form.value as IListing),
    };
  }

  private updateSiteId() {
    this.uploadOptions.data = {
      siteId: this.siteId,
      syncId: this.syncId,
    };
  }

  async loadPhotoFromFile(photo: string): Promise<string> {
    // Working on a physical device.
    if (Capacitor.isNativePlatform()) {
      // Display the photo by reading into base64 format
      return (
        (await this.photoService.loadPhotoForWeb({ path: photo, format: 'jpeg', saved: false }, Directory.Data))
          .webPath || ''
      );
    } else {
      // We don't save locally on the web.
      return '';
    }
  }

  getOfflinePhoto(requestedPhoto: IPhoto): string {
    const photo = this.webPhotos.find((el) => {
      return el.key === requestedPhoto.key;
    });
    if (photo) {
      return photo.uri;
    } else {
      return '';
    }
  }

  removeOfflinePhotoFunction(filename: string) {
    return async () => {
      await this.offlineSitePhotosDbSet.removeByKey(filename as string);
      await this.photoService.removeOfflinePhoto(filename as string);
    };
  }

  imagesUpdated(): () => void {
    return () => {
      this.imageUpdated.emit();
      this.form.markAsDirty();
      if (this.autoSave) {
        this.save();
      }
    };
  }

  getCaptionControl(photo: FormGroup<IPhotoForm>): FormControl {
    return photo.get('caption') as FormControl;
  }

  async saveRecrop(values: FormData) {
    if (this.recropPhotoSelected) {
      const photo: ISubPhoto = {
        key: this.recropPhotoSelected.key,
        caption: values.get('caption') as string,
        cropLeft: parseInt(values.get('cropLeft') as string, 10),
        cropTop: parseInt(values.get('cropTop') as string, 10),
        cropRight: parseInt(values.get('cropRight') as string, 10),
        cropBottom: parseInt(values.get('cropBottom') as string, 10),
        zoomRatio: parseInt(values.get('zoom') as string, 10),
        rotation: parseInt(values.get('rotation') as string, 10),
      };
      this.beforeUpdating.emit(true);
      this.siteFacade.recropImage(this.getListingToSave(), photo);
      this.form.markAsPristine();
      await this.modalController.dismiss();
    } else {
      this.alertFacade.display({
        title: 'Selected Photo Not Found',
        body:
          "Something went wrong and we weren't about to find the photo you tried to crop. If the problem persists, " +
          'please contact support.',
        level: 'error',
      });
      this.logger.error(
        'Recrop Photo Not Found',
        'It looks like the photo that was requested for recropping no longer existed when returning to the listing-photos component.',
      );
    }
  }

  async recropPhoto(photo: FormGroup<IPhotoForm>) {
    this.recropPhotoSelected = photo.value as ISubPhoto;
    const modal = await this.modalController.create({
      component: ImageRecropModalComponent,
      componentProps: {
        photo: photo.value,
        options: {
          ...this.uploadOptions,
          action: NGRX_NO_ACTION,
        },
        saveFunction: (values: FormData) => this.saveRecrop(values),
      },
    });
    await modal.present();
  }
}
